import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Lightbox from 'yet-another-react-lightbox';
import Inline from 'yet-another-react-lightbox/dist/plugins/inline/index.js';

import GalleryThumbnails from './GalleryThumbnails';
import GalleryIcon from './GalleryIcon';
import { imageSrcSet } from './galleryUtils';

import css from './ProductGallery.css';

const ImageButton = props => {
  const { slide, active, index, count, title, onOpen } = props;
  const intl = useIntl();
  const [status, setStatus] = useState('loading');
  const imageRef = useRef(null);

  useEffect(() => {
    const image = imageRef.current;
    setStatus(image && image.complete ? (image.naturalWidth > 0 ? 'loaded' : 'error') : 'loading');
  }, [slide.src]);

  return (
    <button
      type="button"
      data-gallery-open="true"
      className={css.imageButton}
      tabIndex={active ? 0 : -1}
      aria-hidden={active ? undefined : true}
      aria-label={intl.formatMessage({ id: 'ProductGallery.open' }, { title, index: index + 1, count })}
      aria-busy={status === 'loading'}
      onClick={event => onOpen(event, index)}
    >
      <img
        ref={imageRef}
        className={[css.inlineImage, status !== 'loaded' ? css.hiddenImage : ''].join(' ')}
        src={slide.src}
        srcSet={imageSrcSet(slide)}
        sizes="(min-width: 768px) 65vw, calc(100vw - 48px)"
        width={slide.width}
        height={slide.height}
        alt=""
        draggable={false}
        loading={active ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
      {status !== 'loaded' ? (
        <span className={css.imageMessage}>{status === 'error' ? intl.formatMessage({ id: 'ProductGallery.error' }) : intl.formatMessage({ id: 'ProductGallery.loading' })}</span>
      ) : null}
    </button>
  );
};

const GalleryInline = props => {
  const { slides, activeIndex, onSelect, onOpen, suspended, reducedMotion, title } = props;
  const intl = useIntl();
  const gesture = useRef({ pointers: new Set(), start: null, moved: false });

  const pointerDown = event => {
    const current = gesture.current;
    if (!current.pointers.size) {
      current.start = { x: event.clientX, y: event.clientY };
      current.moved = false;
    }
    current.pointers.add(event.pointerId);
    if (current.pointers.size > 1) current.moved = true;
  };

  return (
    <div className={css.inlineLayout}>
      <div
        className={css.stage}
        onPointerDownCapture={pointerDown}
        onPointerMoveCapture={event => {
          const current = gesture.current;
          if (
            current.pointers.has(event.pointerId) &&
            current.start &&
            Math.hypot(event.clientX - current.start.x, event.clientY - current.start.y) > 8
          )
            current.moved = true;
        }}
        onPointerUpCapture={event => gesture.current.pointers.delete(event.pointerId)}
        onPointerCancelCapture={() => {
          gesture.current.pointers.clear();
          gesture.current.moved = true;
        }}
        onClickCapture={event => {
          if (
            event.detail > 0 &&
            gesture.current.moved &&
            event.target.closest('[data-gallery-open]')
          ) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        <Lightbox
          className={css.inline}
          plugins={[Inline]}
          slides={slides}
          index={activeIndex}
          inline={{ style: { width: '100%', height: '100%' } }}
          styles={{ container: { touchAction: 'pan-y', overscrollBehavior: 'auto' } }}
          toolbar={{ buttons: [] }}
          carousel={{ padding: 0, spacing: 0, preload: 1, finite: slides.length <= 1 }}
          animation={{ fade: 0, swipe: reducedMotion ? 0 : 250 }}
          labels={{
            Previous: intl.formatMessage({ id: 'ProductGallery.previous' }),
            Next: intl.formatMessage({ id: 'ProductGallery.next' }),
            'Photo gallery': intl.formatMessage({ id: 'ProductGallery.gallery' }),
            '{index} of {total}': intl.formatMessage({ id: 'ProductGallery.photo' }, { index: '{index}', count: '{total}' }),
          }}
          controller={{ closeOnEscape: false, disableSwipeNavigation: false }}
          on={{
            view: ({ index }) => {
              if (!suspended) onSelect(index);
            },
          }}
          render={{
            iconPrev: () => <GalleryIcon name="left" />,
            iconNext: () => <GalleryIcon name="right" />,
            slide: ({ slide, offset }) => (
              <ImageButton
                key={slide.id}
                slide={slide}
                active={offset === 0}
                index={slides.findIndex(item => item.id === slide.id)}
                count={slides.length}
                title={title}
                onOpen={onOpen}
              />
            ),
            buttonPrev: slides.length <= 1 ? () => null : undefined,
            buttonNext: slides.length <= 1 ? () => null : undefined,
          }}
        />
      </div>

      {slides.length > 0 ? (
        <GalleryThumbnails
          slides={slides}
          activeIndex={activeIndex}
          onSelect={onSelect}
          title={title}
          reducedMotion={reducedMotion}
        />
      ) : null}
    </div>
  );
};

export default GalleryInline;
